<script lang="ts" setup>
import { __ } from '@@/bits/intl'
import OzContainedButton, { OzContainedButtonColorScheme } from '@@/library/v4/components/OzContainedButton.vue'
import OzInput from '@@/library/v4/components/OzInput.vue'
import OzLabelInput from '@@/library/v4/components/OzLabelInput.vue'
import OzLoadingText from '@@/library/v4/components/OzLoadingText.vue'
import { useLibraryLtiStore } from '@@/pinia/library_lti_store'
import { computed, onMounted } from 'vue'

const libraryLtiStore = useLibraryLtiStore()

onMounted(() => {
  // Show timeout screen 5 seconds earlier than the actual token expiration
  const timeoutScreenDelayInSeconds = libraryLtiStore.stateTokenTimeoutSeconds - 5
  setTimeout(() => {
    libraryLtiStore.setExpiryErrorScreen()
  }, timeoutScreenDelayInSeconds * 1000)
})

const isTokenValid = computed((): 'yes' | 'no' => {
  return libraryLtiStore.tokenValidationMessage.length ? 'no' : 'yes'
})

const isLoginWithTokenButtonDisabled = computed((): boolean => {
  return !libraryLtiStore.linkDeploymentToken.length || !!libraryLtiStore.tokenValidationMessage.length
})

function onInputTokenInput(e): void {
  libraryLtiStore.setLinkDeploymentToken(e.target.value)
  // Reset the token validation message to enable the Login button when typing on mobile and tablet
  if (libraryLtiStore.tokenValidationMessage.length) libraryLtiStore.setTokenValidationMessage('')
}
</script>

<template>
  <div class="relative flex flex-col items-center tablet-landscape:items-start w-full h-full">
    <main
      :class="[
        'fixed bottom-0',
        'flex flex-col',
        'box-border w-full p-4 pb-6',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-16',
        'tablet-landscape:ms-auto tablet-landscape:me-[calc(0.5*100vw)] tablet-landscape:rtl:me-auto tablet-landscape:rtl:ms-[calc(0.5*100vw)]',
      ]"
    >
      <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
        <p class="px-2 text-login-body">
          {{ __('You can connect by using a token if you are the owner or admin.') }}
        </p>
      </div>
      <form novalidate class="flex flex-col mb-6" @submit.prevent="libraryLtiStore.loginWithToken">
        <OzLabelInput class="mb-4">
          <template #label>
            <label
              for="token-input"
              :class="['text-body-small-posts', 'text-dark-text-100 dark:text-light-text-100', 'px-2']"
            >
              {{ __('LTI Link Deployment Token') }}
            </label>
          </template>

          <template #input
            ><OzInput
              id="token-input"
              name="token"
              type="text"
              test-id="tokenInput"
              :disabled="libraryLtiStore.isAwaitingServerResponse"
              :model-value="libraryLtiStore.linkDeploymentToken"
              :validation-message="libraryLtiStore.tokenValidationMessage"
              :valid="isTokenValid"
              class="bg-light-ui-100 dark:bg-dark-ui-100"
              @input="onInputTokenInput"
          /></template>
        </OzLabelInput>
        <p
          class="flex items-center justify-center tablet-portrait:justify-start px-2 text-body-small-posts text-dark-text-200 dark:text-light-text-200 mb-4"
        >
          {{ __('You can find your link deployment token in your Dashboard Settings Connections page') }}
        </p>
        <div class="flex justify-center tablet-portrait:justify-end">
          <OzLoadingText
            v-if="libraryLtiStore.isAwaitingServerResponse"
            :text="__('Logging in...')"
            class="text-heading-4 text-center font-semibold px-5 py-2"
          />
          <OzContainedButton
            v-else
            :color-scheme="OzContainedButtonColorScheme.Primary"
            :disabled="isLoginWithTokenButtonDisabled"
            class="w-full tablet-portrait:w-auto"
            >{{ __('Log in to connect') }}</OzContainedButton
          >
        </div>
      </form>
    </main>
  </div>
</template>
